import * as yup from 'yup';

export type CreditCardValidationValues = {
  cardholder_name: string;
  card_number: string;
  card_expiration: string;
  card_cvv: string;
  card_selected: null;
};

const normalizeExpirationDate = (value: unknown): string => {
  const raw = String(value ?? '').replace(/\s/g, '');
  return raw.replace(/(\d{2})[^\d]?(\d{2})/, '$1/$2');
};

const isValidLuhn = (digits: string): boolean => {
  let sum = 0;
  let shouldDouble = false;

  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = Number(digits[index]);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

export const creditCardValidationSchema = yup.object().shape({
  cardholder_name: yup
    .string()
    .required('O nome do titular do cartão é obrigatório.'),
  card_number: yup
    .string()
    .required('O número do cartão é obrigatório.')
    .test('isValidCardNumber', 'Número do cartão inválido.', (value) => {
      if (!value) {
        return false;
      }

      const digits = value.replace(/\s/g, '');
      if (!/^\d+$/.test(digits) || digits.length < 13) {
        return false;
      }

      return isValidLuhn(digits);
    }),
  card_expiration: yup.string().when('card_selected', {
    is: null,
    then: (schema) =>
      schema
        .required('O mês/ano de expiração do cartão é obrigatório.')
        .test('isValidExpiration', 'O vencimento é inválido.', (value) => {
          if (!value) {
            return false;
          }

          const cleanedExpirationDate = normalizeExpirationDate(value);
          if (
            !cleanedExpirationDate ||
            !/^(0[1-9]|1[012])[/]\d{2}$/.test(cleanedExpirationDate)
          ) {
            return false;
          }

          const currentDate = new Date();
          const currentMonth = String(currentDate.getMonth() + 1).padStart(
            2,
            '0',
          );
          const currentYear = String(currentDate.getFullYear()).slice(-2);

          const [expMonth, expYear] = cleanedExpirationDate.split('/');

          if (
            expYear < currentYear ||
            (expYear === currentYear && expMonth < currentMonth)
          ) {
            return false;
          }

          return true;
        }),
  }),
  card_cvv: yup
    .string()
    .required('O código de segurança (CVV) do cartão é obrigatório.')
    .test('isValidCVV', 'Número do CVV inválido.', (value) =>
      Boolean(value && /^[0-9]{3,4}$/.test(value)),
    ),
});
